import { todayLocal } from '@/lib/dates'
import { useState } from 'react'
import { CalendarOff, Pencil, Plus } from 'lucide-react'
import {
  ConfirmDialog, EmptyState, ErrorState, FormDialog, PageHeader, StatusBadge,
} from '@/components/common'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { useBreadcrumb } from '@/hooks/useBreadcrumb'
import { useLookup } from '@/hooks/useLookup'
import { useDocumentTypes } from '@/features/document'
import { toApiProblem } from '@/lib/api/client'
import { useCredentialRuleMutations, useCredentialRules } from '../api'
import { CREDENTIAL_MODE_LABEL } from '../types'
import type { CredentialMode, CredentialRuleView } from '../types'
import { Select } from '@/components/ui/select'
import { DatePicker } from '@/components/ui/date-picker'

interface Draft {
  id?: string
  name: string
  description: string
  documentTypeId: string
  mode: CredentialMode
  nationality: string
  locationId: string
  gradeId: string
  designationId: string
  expiredCountsAsMissing: boolean
  minRemainingDays: string
  effectiveFrom: string
  active: boolean
}

const EMPTY: Draft = {
  name: '',
  description: '',
  documentTypeId: '',
  mode: 'BLOCK',
  nationality: '',
  locationId: '',
  gradeId: '',
  designationId: '',
  expiredCountsAsMissing: true,
  minRemainingDays: '',
  effectiveFrom: todayLocal(),
  active: true,
}

/**
 * "Assignment to this site requires a valid pass."
 *
 * <p>Rules are retired by dating them, never deleted — a placement made last year has to
 * remain explicable against the policy that was in force then. The table therefore shows
 * retired rules too, greyed rather than hidden.
 */
export function CredentialRulesPage() {
  useBreadcrumb()
  const rules = useCredentialRules()
  const types = useDocumentTypes('EMPLOYEE')
  const locations = useLookup<{ id: string; name: string }>('locations')
  const grades = useLookup<{ id: string; name: string }>('grades')
  const designations = useLookup<{ id: string; name: string }>('designations')
  const { create, update, retire } = useCredentialRuleMutations()

  const [draft, setDraft] = useState<Draft | null>(null)
  const [toRetire, setToRetire] = useState<CredentialRuleView | null>(null)
  const [error, setError] = useState<string | null>(null)

  const today = todayLocal()

  async function save() {
    if (!draft) return
    setError(null)
    const body = {
      name: draft.name,
      description: draft.description || null,
      documentTypeId: draft.documentTypeId,
      mode: draft.mode,
      nationality: draft.nationality || null,
      locationId: draft.locationId || null,
      gradeId: draft.gradeId || null,
      designationId: draft.designationId || null,
      expiredCountsAsMissing: draft.expiredCountsAsMissing,
      minRemainingDays: draft.minRemainingDays === '' ? null : Number(draft.minRemainingDays),
      effectiveFrom: draft.effectiveFrom,
      active: draft.active,
    }
    try {
      if (draft.id) {
        await update.mutateAsync({ id: draft.id, ...body })
      } else {
        await create.mutateAsync(body)
      }
      setDraft(null)
    } catch (caught) {
      setError(toApiProblem(caught)?.detail ?? 'That rule could not be saved')
    }
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Credential rules"
        subtitle="Which documents a placement requires, and whether a gap stops it or only flags it."
        actions={
          <Button type="button" onClick={() => { setError(null); setDraft({ ...EMPTY }) }}>
            <Plus className="size-4" aria-hidden="true" />
            Add a rule
          </Button>
        }
      />

      <Card>
        {rules.isPending && <Skeleton className="m-4 h-32" />}
        {rules.isError && <ErrorState error={rules.error} onRetry={() => void rules.refetch()} />}
        {rules.isSuccess && rules.data.length === 0 && (
          <EmptyState
            title="No credential rules"
            description="Add one to stop somebody being placed without the documents that placement needs."
          />
        )}
        {(rules.data?.length ?? 0) > 0 && (
          <div className="overflow-x-auto overscroll-x-contain">
            <table className="w-full min-w-[52rem] text-sm">
              <thead>
                <tr className="border-b border-[var(--color-border)] text-left text-xs uppercase tracking-wide text-[var(--color-muted)]">
                  <th scope="col" className="p-3 font-medium">Rule</th>
                  <th scope="col" className="p-3 font-medium">Requires</th>
                  <th scope="col" className="p-3 font-medium">Applies to</th>
                  <th scope="col" className="p-3 font-medium">Effect</th>
                  <th scope="col" className="p-3 font-medium">In force</th>
                  <th scope="col" className="p-3"><span className="sr-only">Actions</span></th>
                </tr>
              </thead>
              <tbody>
                {rules.data?.map((rule) => {
                  const retired = rule.effectiveTo != null && rule.effectiveTo < today
                  const narrowing = [
                    rule.nationality && `nationality ${rule.nationality}`,
                    rule.locationId && locations.data?.find((l) => l.id === rule.locationId)?.name,
                    rule.gradeId && grades.data?.find((g) => g.id === rule.gradeId)?.name,
                    rule.designationId && designations.data?.find((d) => d.id === rule.designationId)?.name,
                  ].filter(Boolean)

                  return (
                    <tr
                      key={rule.id}
                      className={retired ? 'border-b border-[var(--color-border)] opacity-55 last:border-0' : 'border-b border-[var(--color-border)] last:border-0'}
                    >
                      <td className="p-3 font-medium text-[var(--color-heading)]">{rule.name}</td>
                      <td className="p-3">{rule.documentTypeName ?? '—'}</td>
                      <td className="p-3 text-[var(--color-muted)]">
                        {narrowing.length > 0 ? narrowing.join(', ') : 'everybody in the entity'}
                      </td>
                      <td className="p-3">
                        <StatusBadge
                          status={rule.mode}
                          tone={rule.mode === 'BLOCK' ? 'danger' : 'warning'}
                          label={CREDENTIAL_MODE_LABEL[rule.mode]}
                        />
                        {rule.minRemainingDays != null && (
                          <span className="ms-2 text-xs text-[var(--color-muted)]">
                            needs {rule.minRemainingDays}+ days left
                          </span>
                        )}
                      </td>
                      <td className="p-3 text-xs tabular-nums text-[var(--color-muted)]">
                        {rule.effectiveFrom} → {rule.effectiveTo ?? 'open'}
                      </td>
                      <td className="p-3 text-right">
                        <div className="flex justify-end gap-1">
                          <Button
                            type="button" size="icon" variant="ghost"
                            aria-label={`Edit ${rule.name}`}
                            onClick={() => {
                              setError(null)
                              setDraft({
                                id: rule.id,
                                name: rule.name,
                                description: rule.description ?? '',
                                documentTypeId: rule.documentTypeId,
                                mode: rule.mode,
                                nationality: rule.nationality ?? '',
                                locationId: rule.locationId ?? '',
                                gradeId: rule.gradeId ?? '',
                                designationId: rule.designationId ?? '',
                                expiredCountsAsMissing: rule.expiredCountsAsMissing,
                                minRemainingDays: rule.minRemainingDays?.toString() ?? '',
                                effectiveFrom: rule.effectiveFrom,
                                active: rule.active,
                              })
                            }}
                          >
                            <Pencil className="size-4" aria-hidden="true" />
                          </Button>
                          {!retired && (
                            <Button
                              type="button" size="icon" variant="ghost"
                              aria-label={`Retire ${rule.name}`}
                              onClick={() => setToRetire(rule)}
                            >
                              <CalendarOff className="size-4" aria-hidden="true" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <FormDialog
        open={draft != null}
        onOpenChange={(open) => !open && setDraft(null)}
        title={draft?.id ? `Edit ${draft.name}` : 'Add a credential rule'}
        description="Every narrowing field is optional. Leaving them all blank means the rule applies to everybody in the entity, which is a policy rather than a mistake."
        loading={create.isPending || update.isPending}
        error={error}
        onSubmit={() => void save()}
        size="lg"
      >
        {draft && (
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5 sm:col-span-2">
              <Label htmlFor="rule-name">Name</Label>
              <Input
                id="rule-name" required value={draft.name}
                placeholder="Valid passport on the Jebel Ali site"
                onChange={(event) => setDraft({ ...draft, name: event.target.value })}
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="rule-document">Requires</Label>
              <Select
                id="rule-document" required value={draft.documentTypeId}
                onChange={(event) => setDraft({ ...draft, documentTypeId: event.target.value })}
                className="w-full"
              >
                <option value="">Choose a document…</option>
                {(types.data ?? []).map((type) => (
                  <option key={type.id} value={type.id}>{type.name}</option>
                ))}
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="rule-mode">Effect</Label>
              <Select
                id="rule-mode" value={draft.mode}
                onChange={(event) => setDraft({ ...draft, mode: event.target.value as CredentialMode })}
                className="w-full"
              >
                <option value="BLOCK">{CREDENTIAL_MODE_LABEL.BLOCK}</option>
                <option value="WARN">{CREDENTIAL_MODE_LABEL.WARN}</option>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="rule-location">Only at this location</Label>
              <Select
                id="rule-location" value={draft.locationId}
                onChange={(event) => setDraft({ ...draft, locationId: event.target.value })}
                className="w-full"
              >
                <option value="">Anywhere</option>
                {(locations.data ?? []).map((location) => (
                  <option key={location.id} value={location.id}>{location.name}</option>
                ))}
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="rule-grade">Only at this grade</Label>
              <Select
                id="rule-grade" value={draft.gradeId}
                onChange={(event) => setDraft({ ...draft, gradeId: event.target.value })}
                className="w-full"
              >
                <option value="">Any grade</option>
                {(grades.data ?? []).map((grade) => (
                  <option key={grade.id} value={grade.id}>{grade.name}</option>
                ))}
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="rule-nationality">Only this nationality</Label>
              <Input
                id="rule-nationality" maxLength={2} placeholder="Any" value={draft.nationality}
                onChange={(event) => setDraft({ ...draft, nationality: event.target.value })}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="rule-remaining">Fail if fewer days remain than</Label>
              <Input
                id="rule-remaining" type="number" min={0} placeholder="Only actual expiry"
                value={draft.minRemainingDays}
                onChange={(event) => setDraft({ ...draft, minRemainingDays: event.target.value })}
              />
              <p className="text-xs text-[var(--color-muted)]">
                So nobody is mobilised on Monday to a site their pass stops covering on Friday.
              </p>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="rule-from">In force from</Label>
              <DatePicker
                id="rule-from" required value={draft.effectiveFrom}
                onChange={(event) => setDraft({ ...draft, effectiveFrom: event.target.value })}
              />
            </div>
            <label className="flex items-end gap-2 pb-2 text-sm">
              <input
                type="checkbox"
                checked={draft.expiredCountsAsMissing}
                onChange={(event) => setDraft({ ...draft, expiredCountsAsMissing: event.target.checked })}
              />
              An expired document fails too
            </label>
          </div>
        )}
      </FormDialog>

      <ConfirmDialog
        open={toRetire != null}
        onOpenChange={(open) => !open && setToRetire(null)}
        title={`Retire ${toRetire?.name ?? 'this rule'}?`}
        description="It stops applying from today and keeps applying to placements dated before then. Nothing is deleted."
        confirmLabel="Retire"
        onConfirm={() => {
          if (toRetire) {
            retire.mutate({ id: toRetire.id, lastDay: today }, { onSettled: () => setToRetire(null) })
          }
        }}
      />
    </div>
  )
}
